import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { overlayStore, setPanel } from '../panels.ts';
import { loadRenderSettings, patchRenderSettings } from '../settingsStore.ts';
import { pauseMenuItems, type PauseAction, type PauseScene } from './items.ts';
import '../start/mapSelect.css';
import './menu.css';

export type PauseMenuProps = {
  scene: PauseScene;
  /** Include Restart Race. GameScene sets this while `stage === 'racing'`. */
  racing?: boolean;
  onResume: () => void;
  onRestart?: () => void;
  onEditor: () => void;
  onBoats: () => void;
  onMainMenu: () => void;
};

export type PauseMenu = {
  destroy: () => void;
};

export function isPauseMenuOpen(): boolean {
  return overlayStore.get().pauseMenu !== null;
}

function runAction(props: PauseMenuProps, action: PauseAction): void {
  if (action === 'resume') {
    props.onResume();
    return;
  }

  if (action === 'restart') {
    props.onRestart?.();
    return;
  }

  if (action === 'editor') {
    props.onEditor();
    return;
  }

  if (action === 'boats') {
    props.onBoats();
    return;
  }

  props.onMainMenu();
}

function dialogCoversPause(): boolean {
  const panels = overlayStore.get();
  return (
    panels.editorDialog !== null ||
    panels.editorSaveDialog !== null ||
    panels.editorOpenMap !== null ||
    panels.colorPicker !== null
  );
}

/**
 * Canvas MSAA is a WebGL context attribute, so the choice is stored and picked up by the
 * next `createGameConfig` (page reload). docs/PERFORMANCE.md: 6–12 ms of GPU per frame at
 * 3440×1440 on an integrated GPU.
 */
function SmoothEdgesToggle(): JSX.Element {
  const [smoothEdges, setSmoothEdges] = useState(() => loadRenderSettings().smoothEdges);
  const [touched, setTouched] = useState(false);

  return (
    <div class="pause-setting">
      <button
        type="button"
        class="start-menu-item pause-setting-toggle"
        data-pause-setting="smooth-edges"
        aria-pressed={smoothEdges ? 'true' : 'false'}
        onClick={() => {
          const next = !smoothEdges;
          patchRenderSettings({ smoothEdges: next });
          setSmoothEdges(next);
          setTouched(true);
        }}
      >
        Smooth edges: {smoothEdges ? 'On' : 'Off'}
      </button>
      <p class="pause-setting-hint">
        {touched
          ? 'Takes effect after a page reload.'
          : 'Anti-aliased cliff and gate edges. Off is faster on laptop graphics.'}
      </p>
    </div>
  );
}

export function PauseMenuPanel(props: PauseMenuProps): JSX.Element {
  const { scene } = props;
  const items = pauseMenuItems(scene, { racing: props.racing });
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;

    if (!root) {
      return;
    }

    const buttons = (): HTMLButtonElement[] =>
      [...root.querySelectorAll<HTMLButtonElement>('[data-pause-item]')];

    let index = 0;
    buttons()[0]?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (dialogCoversPause()) {
        return;
      }

      const row = buttons();

      if (row.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        const focused = row.findIndex((button) => button === document.activeElement);
        if (focused >= 0) {
          index = focused;
        }
        const backward = event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey);
        index = (index + (backward ? -1 : 1) + row.length) % row.length;
        row[index]?.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      ref={rootRef}
      class="pause-backdrop ui-chrome"
      role="dialog"
      aria-modal="true"
      aria-label="Paused"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          props.onResume();
        }
      }}
    >
      <div class="pause-stack">
        <div class="map-select-lockup pause-lockup" aria-hidden="true">
          <img src="/assets/helmway-wordmark_text_only.png" alt="" />
        </div>
        <nav class="start-menu" aria-label="Pause">
          {items.map((item) => (
            <button
              key={item.action}
              type="button"
              class="start-menu-item"
              data-pause-item={item.action}
              onClick={() => runAction(props, item.action)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <SmoothEdgesToggle />
      </div>
    </div>
  );
}

export function mountPauseMenu(props: PauseMenuProps): PauseMenu {
  setPanel('pauseMenu', props);

  return {
    destroy: () => setPanel('pauseMenu', null),
  };
}
